import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedCallback } from 'use-debounce';
import { Button, MessageBar, MessageBarBody, MessageBarTitle, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkRegular, DismissRegular, SaveRegular } from '@fluentui/react-icons';

import { ToastContext } from '@shared/contexts';
import { ConfirmDialog } from '@shared/components';

import { AUTOSAVE_STATUS } from '../constants';
import { MOCK_PRODUCTS } from '../data';
import {
  SAFETY_CONCERN,
  TAssessorFunction,
  TChangeCategory,
  TChangeV2,
  TCriticality,
  TSafetyConcern,
  TScopeType,
  TUrgency,
  URGENCY,
} from '../models';
import { getChangeV2AssessmentPath } from '../helpers';
import {
  ChangeDetailsSection,
  RequestDetailsSection,
  InitialScopeSection,
  SupportingInformationSection,
} from '../components/forms';
import { useChangeV2Store } from '../state/ChangeV2Store';
import { canEditRequest } from '../utils';
import { DEFAULT_REQUEST_FORM, TRequestFormData, changeRequestSchema } from '../validation';

type TConfirmState = NonNullable<Parameters<typeof ConfirmDialog>[0]['state']>;

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  actionGroup: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
});

const deriveFamilies = (products: string[]): string[] =>
  Array.from(
    new Set(
      products.map((name) => MOCK_PRODUCTS.find((product) => product.name === name)?.family).filter(Boolean) as string[]
    )
  );

const changeToForm = (change: TChangeV2): TRequestFormData => ({
  title: change.title || 'New change',
  description: change.description,
  reasonOrTrigger: change.reasonOrTrigger,
  desiredOutcome: change.desiredOutcome ?? '',
  implementationProposal: change.implementationProposal ?? '',
  affectedProducts: change.affectedProducts,
  affectedQmsAreas: change.affectedQmsAreas,
  sourceRecord: change.sourceRecord ? [change.sourceRecord] : [],
  urgency: change.urgency ? [change.urgency] : [],
  targetDate: change.targetDate ?? '',
  knownSafetyConcern: change.knownSafetyConcern ?? '',
  safetyConcernDescription: change.safetyConcernDescription ?? '',
  knownAffectedDocuments: change.knownAffectedDocuments,
  relatedChanges: change.relatedChanges,
  collaborators: change.collaborators,
  changeManager: change.changeManager ? [change.changeManager] : [],
  changeCategory: change.changeCategory ? [change.changeCategory] : [],
  scopeType: change.scopeType ? [change.scopeType] : [],
  criticality: change.criticality ? [change.criticality] : [],
  assessorFunctions: change.assessorFunctions,
});

const formToModel = (data: TRequestFormData): Partial<TChangeV2> => ({
  title: data.title.trim() || 'New change',
  description: data.description.trim(),
  reasonOrTrigger: data.reasonOrTrigger.trim(),
  desiredOutcome: data.desiredOutcome?.trim() || undefined,
  implementationProposal: data.implementationProposal?.trim() || undefined,
  affectedProducts: data.affectedProducts,
  affectedProductFamilies: deriveFamilies(data.affectedProducts),
  affectedQmsAreas: data.affectedQmsAreas,
  sourceRecord: data.sourceRecord[0] || undefined,
  urgency: (data.urgency[0] as TUrgency) || URGENCY.NORMAL,
  targetDate: data.targetDate || undefined,
  knownSafetyConcern: (data.knownSafetyConcern as TSafetyConcern) || undefined,
  safetyConcernDescription:
    data.knownSafetyConcern === SAFETY_CONCERN.YES ? data.safetyConcernDescription?.trim() || undefined : undefined,
  knownAffectedDocuments: data.knownAffectedDocuments,
  relatedChanges: data.relatedChanges,
  collaborators: data.collaborators,
  changeManager: data.changeManager[0],
  changeCategory: data.changeCategory[0] as TChangeCategory,
  scopeType: data.scopeType[0] as TScopeType,
  criticality: data.criticality[0] as TCriticality,
  assessorFunctions: data.assessorFunctions as TAssessorFunction[],
});

const ChangeRequestPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { changeId } = useParams();
  const { show } = useContext(ToastContext);
  const {
    getChange,
    changes,
    currentUser,
    role,
    patchRequest,
    recordAutosave,
    submitRequest,
    cancelDraft,
    setAutosaveStatus,
    saveFailureArmed,
  } = useChangeV2Store();

  const change = changeId ? getChange(changeId) : undefined;
  const [confirmState, setConfirmState] = useState<TConfirmState>();

  const editable = change ? canEditRequest(role, change) : false;
  const readOnly = !editable;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, submitCount },
  } = useForm<TRequestFormData>({
    resolver: yupResolver(changeRequestSchema),
    defaultValues: change ? changeToForm(change) : DEFAULT_REQUEST_FORM,
    mode: 'onBlur',
  });

  const debouncedSave = useDebouncedCallback((data: TRequestFormData) => {
    if (!change) return;
    patchRequest(change.id, formToModel(data));
    if (saveFailureArmed) {
      setAutosaveStatus(AUTOSAVE_STATUS.FAILED);
      return;
    }
    recordAutosave(change.id);
    setAutosaveStatus(AUTOSAVE_STATUS.SAVED);
  }, 900);

  // Re-seed the form when navigating to a different change.
  useEffect(() => {
    if (change) reset(changeToForm(change));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change?.id]);

  // Keep the form title in sync when the title is edited from the workspace
  // header, so a header edit is not clobbered by the next autosave.
  useEffect(() => {
    if (change && change.title !== getValues('title')) setValue('title', change.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change?.title]);

  // Autosave after a short debounce whenever an editable field changes.
  useEffect(() => {
    if (!editable) return;
    const subscription = watch((data) => {
      setAutosaveStatus(saveFailureArmed ? AUTOSAVE_STATUS.FAILED : AUTOSAVE_STATUS.SAVING);
      debouncedSave(data as TRequestFormData);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, editable, saveFailureArmed, debouncedSave]);

  if (!change) return null;

  const safetyConcern = watch('knownSafetyConcern');
  const relatedChangeOptions = changes
    .filter((candidate) => candidate.id !== change.id && candidate.displayId)
    .map((candidate) => candidate.displayId as string);

  const errorMessages = Object.values(errors)
    .map((error) => (error as { message?: string })?.message)
    .filter(Boolean) as string[];

  const handleAddAttachment = () => {
    const index = change.attachments.length + 1;
    patchRequest(change.id, {
      attachments: [
        ...change.attachments,
        {
          id: `att-${change.id}-${index}-${change.attachments.length}`,
          name: `supporting-evidence-${index}.pdf`,
          sizeLabel: '480 KB',
          uploadedBy: currentUser,
          uploadedAt: new Date().toISOString(),
        },
      ],
    });
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    patchRequest(change.id, {
      attachments: change.attachments.filter((attachment) => attachment.id !== attachmentId),
    });
  };

  const onValid = (data: TRequestFormData) => {
    const submitDialog: TConfirmState = {
      isOpen: true,
      title: 'Submit request?',
      content: 'The change will move directly to assessment. After submitting, the request becomes read-only.',
      buttonLabel: 'Submit request',
      onConfirm: async () => {
        patchRequest(change.id, formToModel(data));
        submitRequest(change.id);
        setAutosaveStatus(AUTOSAVE_STATUS.SAVED);
        show('success', 'Request submitted. The change is ready for assessment.');
        setConfirmState({ ...submitDialog, isOpen: false });
        navigate(getChangeV2AssessmentPath(change.id));
      },
    };
    setConfirmState(submitDialog);
  };

  const handleSaveDraft = () => {
    patchRequest(change.id, formToModel(getValues()));
    setAutosaveStatus(AUTOSAVE_STATUS.SAVED);
    show('success', 'Draft saved.');
  };

  const handleCancelDraft = () => {
    const cancelDialog: TConfirmState = {
      isOpen: true,
      title: 'Cancel this draft?',
      content: 'The draft will be cancelled and become read-only. This cannot be undone in the prototype.',
      buttonLabel: 'Cancel draft',
      danger: true,
      onConfirm: async () => {
        cancelDraft(change.id);
        show('info', 'Draft cancelled.');
        setConfirmState({ ...cancelDialog, isOpen: false });
      },
    };
    setConfirmState(cancelDialog);
  };

  return (
    <div className={classes.root}>
      {readOnly && change.status !== 'Draft' ? (
        <MessageBar intent="info">
          <MessageBarBody>This request has been submitted and is read-only.</MessageBarBody>
        </MessageBar>
      ) : null}

      {submitCount > 0 && errorMessages.length > 0 ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Please check the entered values</MessageBarTitle>
            {errorMessages.join(' ')}
          </MessageBarBody>
        </MessageBar>
      ) : null}

      <RequestDetailsSection
        control={control}
        errors={errors}
        readOnly={readOnly}
        initiator={change.initiator}
        requestDate={change.requestDate}
      />
      <InitialScopeSection control={control} errors={errors} readOnly={readOnly} safetyConcern={safetyConcern} />
      <SupportingInformationSection
        control={control}
        errors={errors}
        readOnly={readOnly}
        attachments={change.attachments}
        onAddAttachment={handleAddAttachment}
        onRemoveAttachment={handleRemoveAttachment}
        relatedChangeOptions={relatedChangeOptions}
      />
      <ChangeDetailsSection control={control} errors={errors} readOnly={readOnly} />

      {editable ? (
        <div className={classes.actions}>
          <div className={classes.actionGroup}>
            <Button appearance="subtle" icon={<DismissRegular />} onClick={handleCancelDraft}>
              Cancel draft
            </Button>
          </div>
          <div className={classes.actionGroup}>
            <Button appearance="secondary" icon={<SaveRegular />} onClick={handleSaveDraft}>
              Save draft
            </Button>
            <Button appearance="primary" icon={<CheckmarkRegular />} onClick={handleSubmit(onValid)}>
              Submit request
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmDialog state={confirmState} setState={setConfirmState} />
    </div>
  );
};

export default ChangeRequestPage;
